import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Star, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Funnel() {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [name, setName] = useState("");
  const [review, setReview] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    if (!rating) return toast.error("Please select a rating");
    if (!name.trim()) return toast.error("Please add your name");
    if (!review.trim()) return toast.error("Please write your review");
    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, { name: name.trim(), rating, feedback: review.trim() });
      setDone(true);
    } catch (e) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full bg-black/30 border border-white/15 rounded-md px-4 py-3 text-[#F5F5F0] placeholder:text-zinc-500 focus:outline-none focus:border-[#D4AF37] transition-colors duration-200";

  return (
    <div className="funnel-bg grain min-h-screen flex flex-col items-center justify-center px-4 py-12">
      <div className="mb-8 text-center fade-up">
        <div className="mb-3">
          <span className="text-xs tracking-[0.3em] uppercase text-zinc-300">Jeff Harrington</span>
        </div>
        <h1 className="font-display uppercase text-3xl sm:text-4xl text-[#F5F5F0] tracking-tight" data-testid="funnel-title">
          Jeff Harrington Online and In-Person Lessons
        </h1>
        <p className="text-zinc-400 mt-3 text-sm sm:text-base max-w-md mx-auto">
          Saxophone &amp; Jazz Improvisation Lessons — Online &amp; In-Person
        </p>
      </div>

      <div className="glass-card rounded-xl w-full max-w-lg p-8 sm:p-10" data-testid="funnel-card">
        {done ? (
          <div className="fade-up text-center py-6" data-testid="step-thankyou">
            <CheckCircle2 size={56} className="text-[#D4AF37] mx-auto mb-6" strokeWidth={1.5} />
            <h2 className="font-display text-3xl text-[#F5F5F0] mb-4">Thank You, {name.split(" ")[0]}!</h2>
            <p className="text-zinc-400 text-sm leading-relaxed max-w-sm mx-auto">
              Your feedback means a great deal to Jeff.
              {rating >= 4 && " Your review may be featured on jeffharrington.com."}
            </p>
          </div>
        ) : (
          <div className="fade-up space-y-7">
            <div>
              <p className="font-display text-lg text-[#F5F5F0] mb-3">How would you rate your experience?</p>
              <div className="flex items-center gap-4" data-testid="star-rating">
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((n) => {
                    const filled = n <= (hover || rating);
                    return (
                      <button
                        key={n}
                        type="button"
                        data-testid={`star-${n}`}
                        aria-label={`${n} star${n > 1 ? "s" : ""}`}
                        className="star-btn"
                        onMouseEnter={() => setHover(n)}
                        onMouseLeave={() => setHover(0)}
                        onClick={() => setRating(n)}
                      >
                        <Star
                          size={34}
                          strokeWidth={1.5}
                          className={filled ? "text-[#D4AF37]" : "text-zinc-600"}
                          fill={filled ? "#D4AF37" : "none"}
                        />
                      </button>
                    );
                  })}
                </div>
                <span className="text-zinc-500 text-sm" data-testid="rating-hint">
                  {rating ? `${rating} out of 5` : "Select your rating"}
                </span>
              </div>
            </div>

            <div>
              <p className="font-display normal-case text-lg text-[#F5F5F0] mb-3">Your name</p>
              <input
                data-testid="name-input"
                className={inputCls}
                placeholder="Please write your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            <div>
              <p className="font-display normal-case text-lg text-[#F5F5F0] mb-3">Tell us about your experience</p>
              <textarea
                data-testid="feedback-input"
                className={`${inputCls} min-h-[160px] resize-none`}
                placeholder="Start your review..."
                value={review}
                onChange={(e) => setReview(e.target.value)}
              />
            </div>

            <button
              data-testid="submit-review-btn"
              onClick={submit}
              disabled={submitting}
              className="gold-btn font-display normal-case w-full py-3 rounded-md text-lg tracking-wide"
            >
              {submitting ? "Submitting..." : "Submit review"}
            </button>
          </div>
        )}
      </div>

      <p className="text-zinc-600 text-xs mt-10">© 2026 Jeff Harrington. All rights reserved.</p>
    </div>
  );
}
